import cardSlide1 from "../assets/audio/cards/card-slide-1.ogg";
import cardSlide2 from "../assets/audio/cards/card-slide-2.ogg";
import cardSlide3 from "../assets/audio/cards/card-slide-3.ogg";
import cardSlide4 from "../assets/audio/cards/card-slide-4.ogg";
import cardPlace1 from "../assets/audio/cards/card-place-1.ogg";
import cardPlace2 from "../assets/audio/cards/card-place-2.ogg";
import cardPlace3 from "../assets/audio/cards/card-place-3.ogg";
import cardPlace4 from "../assets/audio/cards/card-place-4.ogg";
import cardShove1 from "../assets/audio/cards/card-shove-1.ogg";
import cardShove2 from "../assets/audio/cards/card-shove-2.ogg";
import cardShove3 from "../assets/audio/cards/card-shove-3.ogg";
import cardShove4 from "../assets/audio/cards/card-shove-4.ogg";
import cardShuffleUrl from "../assets/audio/cards/card-shuffle.ogg";
import fireCrackleUrl from "../assets/audio/ambience/fire-crackle.ogg";
import victoryFanfareUrl from "../assets/audio/victory/victory-fanfare.ogg";
import musicUrl from "../assets/audio/midnight-tale.mp3";

const CARD_SLIDE_URLS = [cardSlide1, cardSlide2, cardSlide3, cardSlide4];
const CARD_PLACE_URLS = [cardPlace1, cardPlace2, cardPlace3, cardPlace4];
const CARD_SHOVE_URLS = [cardShove1, cardShove2, cardShove3, cardShove4];

/**
 * Moteur audio : cartes, feu de cheminee, fanfare de victoire et musique de
 * fond enregistres (voir LICENSE.txt dans chaque sous-dossier de
 * src/assets/audio/ — Kenney "Casino Audio" CC0 pour les cartes et le feu,
 * cynicmusic CC0 pour la fanfare, Kevin MacLeod "Midnight Tale" CC BY 4.0
 * pour la musique), le reste (boutons, blocage, annulation, murmure de
 * salle) synthetise a la volee (Web Audio API), memes principes que
 * dkg-battle/dkg-farkle. Le solitaire se joue souvent son bas ou coupe
 * (cahier des charges §9) : tous les effets sont courts et discrets, jamais
 * agacants en boucle. Si le decodage d'un enregistrement echoue (navigateur
 * qui ne supporte pas le format), repli silencieux sur les versions
 * synthetisees d'origine : aucune fonctionnalite perdue (sauf la musique et
 * l'ambiance de feu, qui restent alors muettes comme dans dkg-farkle).
 */
export class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambience: { stop: () => void } | null = null;
  private musicEl: HTMLAudioElement | null = null;

  private slideBuffers: AudioBuffer[] = [];
  private placeBuffers: AudioBuffer[] = [];
  private shoveBuffers: AudioBuffer[] = [];
  private shuffleBuffer: AudioBuffer | null = null;
  private fireBuffer: AudioBuffer | null = null;
  private fireSource: AudioBufferSourceNode | null = null;
  private ambienceRequested = false;

  /** A appeler suite a un geste utilisateur (les navigateurs l'exigent). */
  ensureStarted(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    const master = ctx.createGain();
    master.gain.value = 0.6;
    master.connect(ctx.destination);
    this.ctx = ctx;
    this.master = master;
    void this.loadCardBuffers(ctx);
  }

  private async loadCardBuffers(ctx: AudioContext): Promise<void> {
    const decodeAll = async (urls: string[]) => {
      const results = await Promise.allSettled(
        urls.map(async (url) => {
          const res = await fetch(url);
          const data = await res.arrayBuffer();
          return await ctx.decodeAudioData(data);
        }),
      );
      return results
        .filter((r): r is PromiseFulfilledResult<AudioBuffer> => r.status === "fulfilled")
        .map((r) => r.value);
    };
    this.slideBuffers = await decodeAll(CARD_SLIDE_URLS);
    this.placeBuffers = await decodeAll(CARD_PLACE_URLS);
    this.shoveBuffers = await decodeAll(CARD_SHOVE_URLS);
    this.shuffleBuffer = (await decodeAll([cardShuffleUrl]))[0] ?? null;
    this.fireBuffer = (await decodeAll([fireCrackleUrl]))[0] ?? null;
    if (this.ambienceRequested) this.startFireLoop();
  }

  /** Joue une variante au hasard d'un jeu d'enregistrements, avec un leger jitter de hauteur. */
  private playSample(buffers: AudioBuffer[], volume: number, rateJitter = 0.08): boolean {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || buffers.length === 0) return false;
    const buffer = buffers[Math.floor(Math.random() * buffers.length)]!;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 1 - rateJitter / 2 + Math.random() * rateJitter;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain).connect(master);
    source.start();
    return true;
  }

  // ------------------------------------------------------------- effets

  /** Carte saisie : glissement enregistre, ou repli synthetise. */
  cardPick(): void {
    if (this.playSample(this.slideBuffers, 0.42)) return;
    this.noiseHit(1100 + Math.random() * 300, 0.05, 0.08);
  }

  /** Carte posee sur la table : choc enregistre, ou repli synthetise. */
  cardPlace(): void {
    if (this.playSample(this.placeBuffers, 0.45)) return;
    this.knock(200, 0.07, 0.22);
    this.noiseHit(1400, 0.07, 0.08);
  }

  /** Carte distribuee (cascade initiale) : glissement enregistre plus discret. */
  cardDeal(): void {
    if (this.playSample(this.slideBuffers, 0.16, 0.14)) return;
    this.noiseHit(1600 + Math.random() * 500, 0.045, 0.07);
  }

  /** Melange initial, une seule fois avant la distribution. */
  cardShuffle(): void {
    const ctx = this.ctx;
    const master = this.master;
    const buffer = this.shuffleBuffer;
    if (!ctx || !master || !buffer) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 0.4;
    source.connect(gain).connect(master);
    source.start();
  }

  /** Carte retournee : enregistrement plus sec (shove), ou repli synthetise ("swish" balaye). */
  cardFlip(): void {
    if (this.playSample(this.shoveBuffers, 0.4)) return;
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    const dur = 0.14;
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer(ctx, dur);
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.setValueAtTime(700, now);
    bandpass.frequency.exponentialRampToValueAtTime(2600, now + dur);
    bandpass.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.13, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    noise.connect(bandpass).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + dur);
  }

  /** Carte posee sur une fondation : pose enregistree + petit tintement, ou repli synthetise. */
  foundationPlace(): void {
    if (!this.playSample(this.placeBuffers, 0.45)) this.knock(320, 0.06, 0.24);
    this.coinClinks(1);
  }

  /** Depot illegal : retour elastique, "toc" sourd et neutre. */
  invalidMove(): void {
    this.knock(120, 0.09, 0.18);
  }

  /** Annulation d'un coup : glissement enregistre plus grave, ou repli synthetise. */
  undo(): void {
    if (this.playSample(this.slideBuffers, 0.32, 0.08)) return;
    this.noiseHit(700, 0.07, 0.09);
  }

  buttonClick(): void {
    this.knock(140, 0.1, 0.3);
  }

  /** Blocage detecte : deux tons descendants, doux — pas de sanction sonore. */
  blocked(): void {
    this.arpeggio([294, 247], 0.16, 0.14);
  }

  /** Rebond d'une carte pendant la cascade de victoire. */
  cascadeBounce(): void {
    this.knock(260 + Math.random() * 160, 0.05, 0.1);
  }

  /** Victoire : fanfare enregistree ("Victory Fanfare Short" par cynicmusic, CC0), jouee hors du graphe Web Audio (comme la musique), + pieces qui s'entrechoquent. */
  victory(): void {
    const audio = new Audio(victoryFanfareUrl);
    audio.volume = 0.7;
    void audio.play();
    this.coinClinks(7);
  }

  // ------------------------------------------------------------- ambiance

  /** Ambiance de taverne : feu qui crepite (enregistrement reel, cf.
   * startFireLoop) + murmure de salle synthetise, encore plus discrets que
   * dans Battle (personne ne commente, cahier des charges §9). */
  startAmbience(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || this.ambience) return;

    const murmur = ctx.createBufferSource();
    murmur.buffer = this.brownNoiseBuffer(ctx, 4);
    murmur.loop = true;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = 300;
    const murmurGain = ctx.createGain();
    murmurGain.gain.value = 0.025;
    murmur.connect(lowpass).connect(murmurGain).connect(master);
    murmur.start();

    this.ambience = {
      stop: () => {
        murmur.stop();
        this.stopFireLoop();
        this.ambience = null;
      },
    };
    this.startFireLoop();
  }

  /**
   * Boucle de feu de cheminee, vrai enregistrement (Kenney "Casino Audio",
   * CC0 — meme fichier que dkg-farkle, voir
   * src/assets/audio/ambience/LICENSE.txt) via AudioBufferSourceNode (loop
   * natif Web Audio, plus propre qu'un <audio loop> pour un bouclage sans
   * accroc). Gain releve a 1.8 : l'enregistrement source est a bas niveau.
   * Si le buffer n'est pas encore decode, la demande est memorisee et la
   * boucle demarre des que loadCardBuffers() termine.
   */
  private startFireLoop(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || this.fireSource) return;
    if (!this.fireBuffer) {
      this.ambienceRequested = true;
      return;
    }
    this.ambienceRequested = false;
    const source = ctx.createBufferSource();
    source.buffer = this.fireBuffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 1.8;
    source.connect(gain).connect(master);
    source.start();
    this.fireSource = source;
  }

  private stopFireLoop(): void {
    this.ambienceRequested = false;
    try {
      this.fireSource?.stop();
    } catch {
      /* deja arrete */
    }
    this.fireSource = null;
  }

  /**
   * Musique de fond : vrai enregistrement ("Midnight Tale" par Kevin
   * MacLeod, incompetech.com, CC BY 4.0 — voir src/assets/audio/LICENSE.txt),
   * jouee en boucle via un <audio> natif (comme dkg-farkle), avec un fondu
   * d'entree. Theme calme au luth/guitare, volume bas par defaut (cahier
   * des charges §9).
   */
  startMusic(volume = 0.35): void {
    if (!this.musicEl) {
      const audio = new Audio(musicUrl);
      audio.loop = true;
      audio.volume = 0;
      audio.preload = "auto";
      this.musicEl = audio;
    }
    const audio = this.musicEl;
    void audio.play().catch(() => {
      /* lecture bloquee tant qu'il n'y a pas eu de geste utilisateur : sans consequence, on reessaiera. */
    });
    const target = volume;
    const start = performance.now();
    const fade = () => {
      const t = Math.min(1, (performance.now() - start) / 1500);
      audio.volume = target * t;
      if (t < 1) requestAnimationFrame(fade);
    };
    fade();
  }

  stopMusic(): void {
    this.musicEl?.pause();
  }

  // ------------------------------------------------------------- interne

  private arpeggio(freqs: number[], gap: number, volume: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      const t0 = now + i * gap;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(volume, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
      osc.connect(gain).connect(master);
      osc.start(t0);
      osc.stop(t0 + 0.55);
    });
  }

  /** Tintements metalliques (tons inharmoniques tres courts). */
  private coinClinks(count: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    for (let i = 0; i < count; i++) {
      const t0 = now + i * (0.04 + Math.random() * 0.07);
      const base = 2200 + Math.random() * 1800;
      for (const mult of [1, 1.51, 2.32]) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = base * mult;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.04 / mult, t0 + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
        osc.connect(gain).connect(master);
        osc.start(t0);
        osc.stop(t0 + 0.15);
      }
    }
  }

  /** Petit choc sec : bruit filtre + court sinus descendant. */
  private knock(freq: number, decay: number, volume: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;

    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer(ctx, decay);
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.value = freq * 3;
    bandpass.Q.value = 0.8;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(volume * 0.5, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + decay);
    noise.connect(bandpass).connect(noiseGain).connect(master);
    noise.start(now);
    noise.stop(now + decay);

    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.6, now + decay);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(volume, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + decay);
    osc.connect(oscGain).connect(master);
    osc.start(now);
    osc.stop(now + decay);
  }

  /** Bruit filtre pur, sans ton (effleurements, frottements). */
  private noiseHit(freq: number, dur: number, volume: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer(ctx, dur);
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.value = freq;
    bandpass.Q.value = 0.9;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    noise.connect(bandpass).connect(gain).connect(master);
    noise.start(now);
    noise.stop(now + dur);
  }

  private noiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
    const buffer = ctx.createBuffer(1, Math.max(1, ctx.sampleRate * duration), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  /** Bruit brownien (grave, doux) pour le murmure de salle. */
  private brownNoiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
    const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    return buffer;
  }
}
